import {
  ComponentProps,
  DetailedHTMLProps,
  ElementType,
  HTMLAttributes,
  ReactNode,
  useId,
} from 'react';

import { Input } from '../Input/Input';
import { Typography } from '../Typography/Typography';

import { BigLabel, Description, Error, Label, Root } from './InputField.ui';

export type InputFieldProps<InputComponent extends ElementType = typeof Input> =
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
    bigLabel?: ReactNode;
    description?: ReactNode;
    error?: ReactNode;
    InputComponent?: InputComponent;
    inputProps?: ComponentProps<InputComponent>;
    label?: ReactNode;
  };

export const InputField = <InputComponent extends ElementType = typeof Input>(
  props: InputFieldProps<InputComponent>,
) => {
  const {
    bigLabel,
    description,
    error,
    inputProps = {} as ComponentProps<InputComponent>,
    InputComponent = Input,
    label,
    children,
    ...restInputFieldProps
  } = props;

  const generatedId = useId();
  const inputId = inputProps.id ?? generatedId;

  return (
    <Root {...restInputFieldProps}>
      {bigLabel && (
        <BigLabel htmlFor={inputId}>
          <Typography.Text>{bigLabel}</Typography.Text>
        </BigLabel>
      )}

      {label && (
        <Label htmlFor={inputId}>
          <Typography.H4 as="span">{label}</Typography.H4>
        </Label>
      )}

      {children ? (
        children
      ) : (
        <InputComponent
          {...inputProps}
          isInvalid={Boolean(error)}
          id={inputId}
        />
      )}

      {description && <Description as="div">{description}</Description>}

      {error && <Error as="div">{error}</Error>}
    </Root>
  );
};
